/**
 * 访客星球（/world）与居民公开主页共用的展示规则。只把后端给的公开事实换成人话，不猜、不补：
 * - 此刻在做什么只用服务端的 `doing`；在哪只用 `place_name`、`residence`、`city`；
 * - 公开接口没有任何坐标，这里也不产生坐标（不按城市名查、不按驿站名配）；
 * - 头像：有公开照片用照片；没有时是中性的爪印底（10-03 起不再用物种插画顶替）。演示小灰猫只在演示模式、只给猫用，
 *   绝不拿别的物种的照片顶替，也不冒充 live 里的真实居民。
 */
import type { PetOrigin, PetPresence, PetSpecies, Post, PublicResident, ResidentKind } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { petPortraitUrl } from "@/shared/ui";

/** 物种的中文叫法：表挪到了 shared/ui（共享头像 PetAvatar 的插画读屏也要用），这里原样转出，已有的引用不用改。 */
export { speciesName } from "@/shared/ui";

export type PresenceGroup = "home" | "out" | "road" | "unknown";

/** 只按服务端给的 presence 分组，不从 doing 文字里猜。 */
export function presenceGroup(presence: PetPresence): PresenceGroup {
  switch (presence) {
    case "at_home":
      return "home";
    case "visiting":
    case "at_destination":
      return "out";
    case "in_transit":
    case "returning":
      return "road";
    default:
      return "unknown";
  }
}

/**
 * 此刻那一行：服务端的 doing；到访的地点名已经写在 doing 里就不再重复，没写到才用括号补上。
 * 不用分隔点拼接：服务端的名字里自己就带“·”（如“星球居民驿站·中环”），再拼一个会在同一行里出现两种样子的点。
 */
export function nowLine(resident: Pick<PublicResident, "doing" | "place_name">): string {
  const doing = resident.doing.trim();
  const place = resident.place_name?.trim();
  return place && !doing.includes(place) ? `${doing}（${place}）` : doing;
}

/**
 * 住在哪的两段：驿站名，和要补在括号里的城市（城市为空或已写在驿站名里就是 null）。
 * 显示成“星球居民驿站·西贡海边（香港）”，由 ResidentHome 排版（“（城市）”整体不断行）。
 */
export function homeParts(resident: Pick<PublicResident, "residence" | "city">): { residence: string; city: string | null } {
  const residence = resident.residence.trim();
  const city = resident.city.trim();
  return { residence, city: city && !residence.includes(city) ? city : null };
}

/** 相对时间，以服务器时间为准（访客手机的时钟可能不准）。 */
export function agoText(iso: string, nowMs: number): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then) || !Number.isFinite(nowMs)) return "";
  const minutes = Math.floor((nowMs - then) / 60_000);
  if (minutes < 1) return "刚刚";
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  const date = new Date(then);
  return `${date.getMonth() + 1} 月 ${date.getDate()} 日`;
}

/**
 * 星球上最近的小事：去掉已经在居民卡上出现过的，免得同一句话出现两次。
 * 同一位作者、一字不差的同一句话也算出现过（后台会在不同时间记下同样的一句），列表里也只留最新的一条。
 */
export function extraPosts(posts: Post[], residents: PublicResident[]): Post[] {
  const line = (post: Post) => `${post.author.actor_id}\n${post.text.trim()}`;
  const shownIds = new Set<string>();
  const seen = new Set<string>();
  for (const resident of residents) {
    const post = resident.recent_posts[0];
    if (post) {
      shownIds.add(post.post_id);
      seen.add(line(post));
    }
  }
  const out: Post[] = [];
  for (const post of posts) {
    if (shownIds.has(post.post_id) || seen.has(line(post))) continue;
    seen.add(line(post));
    out.push(post);
  }
  return out;
}

/**
 * 读屏怎么说这张照片：原创居民（origin = adopted_original）的形象是生成的原创设计，不是照片
 * （契约 PublicResident.avatar_url 的说明），读作“{名字}的形象（AI 生成）”；其余读作“{名字}的照片”；
 * 演示小灰猫读作“（演示照片）”。只改读屏，画面上不加角标（2026-09-24 主窗口定）。
 */
export function portraitPhotoLabel(name: string, origin: PetOrigin | null | undefined, demo: boolean): string {
  if (demo) return `${name}（演示照片）`;
  return origin === "adopted_original" ? `${name}的形象` : `${name}的照片`;
}

export type PortraitSource = { kind: "photo"; src: string; demo: boolean } | { kind: "species"; species: PetSpecies };

/**
 * 居民头像来源：公开照片 → 照片；演示模式里的猫 → 授权的演示小灰猫；其余 → 同物种插画。
 * 演示小灰猫的地址仍由全站唯一规则 petPortraitUrl 给出（live 模式它返回 null）。
 */
export function residentPortrait(species: PetSpecies, photoUrl: string | null | undefined): PortraitSource {
  if (photoUrl) return { kind: "photo", src: photoUrl, demo: false };
  if (env.dataMode === "fixture" && species === "cat") {
    const demo = petPortraitUrl(null);
    if (demo) return { kind: "photo", src: demo, demo: true };
  }
  return { kind: "species", species };
}

/** 原创角色的标签字样（契约 PublicResident.resident_kind 的说明：页面标「PetSoul 原创角色」）。 */
export const ORIGINAL_CHARACTER_TAG = "PetSoul 原创角色";

/**
 * 居民类别（2026-09-27，世界运营第一期 D5）：adoptable＝可以领养的居民；public_npc＝PetSoul 原创角色，不参加领养，
 * 只出现在自己的公开主页上（领养名单 GET /public/residents 里没有它）。
 * 字段缺省（旧数据、还没带这一栏的回包）当 adoptable；没有居民那一栏（已经被领养走、或只是主页公开的宠物）是 null。
 */
export function residentKind(resident: Pick<PublicResident, "resident_kind"> | null | undefined): ResidentKind | null {
  if (!resident) return null;
  return resident.resident_kind ?? "adoptable";
}

/** 是不是 PetSoul 原创角色：名字旁标“PetSoul 原创角色”，页面上不出任何领养入口。 */
export function isOriginalCharacter(resident: Pick<PublicResident, "resident_kind"> | null | undefined): boolean {
  return residentKind(resident) === "public_npc";
}

/** 后台设的官方形象照（契约 PublicResident.official_avatar）读作“{名字}的官方形象”：官方形象照不一定是生成的，不按来历推断“AI 生成”。 */
export function officialPortraitLabel(name: string): string {
  return `${name}的官方形象`;
}

/** 头像的一级：一张照片（地址和读屏说法），或最后的中性底（10-03 起不再用物种插画顶替，用户「图像上的插画给删除掉！」）。 */
export type PortraitStep = { kind: "photo"; src: string; label: string } | { kind: "blank"; label: string };

/**
 * 这位居民有没有自己的形象（官方形象照或原来那张照片）：用户 10-03「没有形象的都不要出现在星球了」——访客星球页、领养页只显示有形象的。
 * 只看地址在不在；地址在但打不开，头像退到中性底（ResidentPortrait），人照样显示。
 */
export function hasOwnImage(p: { avatar_url?: string | null; fallback_avatar_url?: string | null; photo_url?: string | null }): boolean {
  return Boolean(p.avatar_url || p.fallback_avatar_url || p.photo_url);
}

/**
 * 头像的退回链（2026-09-27 D5 续；10-03 最后一级改成中性底）：官方形象照（photoUrl，official 为真）→ TA 原来那张照片（fallbackUrl）→ 中性底，
 * 前一级打不开才用后一级。每一级带自己的读屏说法：官方形象照“{名字}的官方形象”；退回原来的照片后，读那张照片原来的说法；
 * 中性底见下面那句。空的地址、和上一级同一个地址的不算一级。两张都没有时：演示模式里的猫用授权的演示小灰猫（见 residentPortrait），其余直接中性底。
 * fallbackUrl、official 缺省（旧后端、领养页）当 null / false：和原来的画法一样。
 */
export function portraitChain(p: { name: string; species: PetSpecies; photoUrl?: string | null; fallbackUrl?: string | null; official?: boolean; origin?: PetOrigin | null }): PortraitStep[] {
  const steps: PortraitStep[] = [];
  if (p.photoUrl) steps.push({ kind: "photo", src: p.photoUrl, label: p.official ? officialPortraitLabel(p.name) : portraitPhotoLabel(p.name, p.origin, false) });
  if (p.fallbackUrl && p.fallbackUrl !== p.photoUrl) steps.push({ kind: "photo", src: p.fallbackUrl, label: portraitPhotoLabel(p.name, p.origin, false) });
  if (!steps.length) {
    const source = residentPortrait(p.species, null);
    if (source.kind === "photo") steps.push({ kind: "photo", src: source.src, label: portraitPhotoLabel(p.name, p.origin, true) });
  }
  // 最后一级：中性底，不画物种插画（10-03）。有照片只是打不开时说“照片暂时打不开”，本来就没有照片说“暂无照片”（UI-BASE-01b：全站一套说法）
  steps.push({ kind: "blank", label: steps.length ? `${p.name}，照片暂时打不开` : `${p.name}，暂无照片` });
  return steps;
}
